import React, { createContext, useRef } from "react"
import { createPortal } from "react-dom"
import { ActionIcon, Menu, Textarea, TextInput, Tooltip, UnstyledButton, useMantineTheme } from "@mantine/core"
import { Icon } from "@/components"
import { SearchPopdownStandard } from "@/components/TopNavigation/components/SearchPopdown/SearchPopdownStandard"
import { SearchPopdownAi } from "@/components/TopNavigation/components/SearchPopdown/SearchPopdownAi"
import { useStyles } from "@/components/TopNavigation/components/SearchBarGroup/SearchBarGroup.styles"
import { useEscapeKey, useOnClickOutsideMultipleRefs } from "app/static/frontend/imports/hooks"

import {
    DESKTOP_POPDOWN_CONTAINER_WIDTH,
    TABLET_POPDOWN_CONTAINER_WIDTH,
    topNavigationFixedHeight,
} from "app/static/frontend/design-constants"
import { AdvancedSearchTipsModal } from "@/components/TopNavigation/components/AdvancedSearchTipsModal/AdvancedSearchTipsModal"
import { useDisclosure } from "@mantine/hooks"
import { useResponsiveSize } from "@/hooks"
import { SearchBarGroupProps } from "@/components/TopNavigation/types"

export const SearchContext = createContext(null)

export const SearchBarGroup = (props: SearchBarGroupProps) => {
    const {
        aiSearchButtonIsVisible,
        aiSearchCancelOnClick,
        aiSearchExampleOnClick,
        aiSearchExamples,
        aiSearchIsEnabled,
        aiSearchStatus,
        setAiSearchStatus,
        aiSearchToggle,
        clearButtonOnClick,
        datasetsOptions,
        favoriteButtonDisabled,
        favoriteButtonHidden,
        favoriteButtonOnClick,
        favoriteButtonTooltip,
        filtersSelected,
        gridItemOnClick,
        onCloseSearchPopdown,
        popdownIsOpen,
        searchButtonOnClick,
        searchDisabled,
        searchInputDefaultValue,
        searchInputOnChange,
        // Opens Search Popdown
        searchInputOnClick,
        searchInputOnInput,
        searchInputOnKeyDown,
        searchInputOnKeyUp,
        searchInputPlaceholder,
        searchInputValue,
        searchTextInputRef,
        segueToSearch,
        setSearchTerm,
        segueToProfile,
        favorites,
        recents,
        favoriteUserAction,
        unfavoriteUserAction,
        archiveUserAction,
        clearRecents,
    } = props

    const { isLarge, isMedium } = useResponsiveSize()

    const { classes } = useStyles({
        aiSearchButtonIsVisible,
        favoriteButtonDisabled,
        favoriteButtonHidden,
        loggedInAsUser: Boolean(Userdata.logged_in_as_user) || Boolean(Userdata.logged_in_as_user_from_regular_user),
        popdownIsOpen,
        searchInputValue,
    })
    const theme = useMantineTheme()
    const searchPopdownRef = useRef()
    const searchInputContainerRef = useRef()

    useOnClickOutsideMultipleRefs([searchPopdownRef, searchInputContainerRef], onCloseSearchPopdown)
    useEscapeKey(onCloseSearchPopdown)
    const [isSearchTipsOpen, searchTips] = useDisclosure(false)

    const textareaOffsetHeight = searchTextInputRef.current && searchTextInputRef.current.offsetHeight

    const popdownOffset =
        textareaOffsetHeight && textareaOffsetHeight > topNavigationFixedHeight
            ? textareaOffsetHeight - topNavigationFixedHeight
            : 0

    const popdownContainerElement = document.getElementById("popdown-portal")

    if (popdownContainerElement) {
        if (popdownIsOpen) {
            document.body.style.overscrollBehavior = "none"
            popdownContainerElement.style.backdropFilter = "blur(8px)"
            popdownContainerElement.style["-webkit-backdrop-filter"] = "blur(8px)"
            popdownContainerElement.style.background = "rgba(255, 255, 255, 0.6)"
            popdownContainerElement.style.height = "100vh"
            popdownContainerElement.style.display = "block"

            if (searchInputValue === "") {
                popdownContainerElement.style.paddingTop = `${topNavigationFixedHeight}px`
            } else {
                popdownContainerElement.style.paddingTop = `${topNavigationFixedHeight + popdownOffset}px`
            }
            // adding this padding left to bring the modal to where the search bar is if isMedium is true
            popdownContainerElement.style.paddingLeft = isMedium ? "4rem" : "0px"
            popdownContainerElement.style.paddingRight = isMedium ? "1rem" : "0"
            popdownContainerElement.style.minWidth = isLarge
                ? DESKTOP_POPDOWN_CONTAINER_WIDTH
                : TABLET_POPDOWN_CONTAINER_WIDTH
        } else {
            document.body.style.overscrollBehavior = "unset"
            popdownContainerElement.style.backdropFilter = "unset"
            popdownContainerElement.style.display = "none"
        }
    }

    const handleClearButtonClick = () => {
        // reset TopNavigation Popdown Container position
        popdownContainerElement.style.paddingTop = `${topNavigationFixedHeight}px`
        clearButtonOnClick()
        aiSearchIsEnabled && setAiSearchStatus("main")
    }

    // for Standard vs. AI Search
    const searchIconsDropdown = aiSearchButtonIsVisible ? (
        <Menu
            classNames={{
                dropdown: classes.dropdown,
                item: classes.item,
                itemIcon: classes.itemIcon,
                itemLabel: classes.itemLabel,
            }}
            offset={-35}
            position="bottom"
        >
            <Menu.Target>
                <UnstyledButton className={classes.searchDropdownButton} aria-label="Change search type">
                    <Icon
                        icon={aiSearchIsEnabled ? "sparkles" : "search"}
                        iconFamily={aiSearchIsEnabled ? "fas" : "far"}
                        color={aiSearchIsEnabled && popdownIsOpen ? theme.colors.violet[5] : null}
                    />
                    <span className={classes.angleDownIcon}>
                        <Icon icon="angle-down" />
                    </span>
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown role="menu">
                <Menu.Item
                    data-plum-id="top-bar-search-option-standard"
                    icon={<Icon icon="search" />}
                    onClick={() => aiSearchIsEnabled && aiSearchToggle()}
                    rightSection={!aiSearchIsEnabled && <Icon icon="check" color={theme.colors.gray[7]} />}
                >
                    Search
                </Menu.Item>
                <Menu.Item
                    data-plum-id="top-bar-search-option-ai"
                    icon={<Icon icon="sparkles" iconFamily="fas" color={theme.colors.violet[5]} />}
                    onClick={() => !aiSearchIsEnabled && aiSearchToggle()}
                    rightSection={aiSearchIsEnabled && <Icon icon="check" color={theme.colors.gray[7]} />}
                >
                    AI Search
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    ) : (
        // For cases where AI search is not available and Standard search is the only option
        <UnstyledButton
            className={classes.searchStandardOnlyButton}
            disabled={searchDisabled}
            onClick={searchButtonOnClick}
        >
            <Icon icon="search" />
        </UnstyledButton>
    )

    const rightSectionButtons = (
        <div className={classes.rightSectionButtons}>
            {(searchInputValue || filtersSelected) && !favoriteButtonHidden && (
                <Tooltip label={favoriteButtonTooltip}>
                    <ActionIcon
                        aria-disabled={favoriteButtonDisabled}
                        aria-label="Create favorite for search"
                        radius="xs"
                        onClick={favoriteButtonDisabled ? null : favoriteButtonOnClick}
                        className={classes.favoriteButton}
                    >
                        {favoriteButtonDisabled ? <Icon icon="star" iconFamily="fas" /> : <Icon icon="star" />}
                    </ActionIcon>
                </Tooltip>
            )}
            {searchInputValue && (
                <ActionIcon
                    aria-label="Clear search terms"
                    radius="xs"
                    onClick={handleClearButtonClick}
                    className={classes.clearButton}
                >
                    <Icon icon="times" />
                </ActionIcon>
            )}
        </div>
    )

    const sharedSearchInputProps = {
        "data-plum-id": "top-bar-search-bar",
        className: classes.textarea,
        classNames: {
            input: classes.input,
            icon: classes.leftSectionIcon,
        },
        defaultValue: searchInputDefaultValue,
        onChange: searchInputOnChange,
        onInput: searchInputOnInput,
        onKeyDown: searchInputOnKeyDown,
        onKeyUp: searchInputOnKeyUp,
        placeholder: searchInputPlaceholder,
        // This Mantine 6 `icon` prop with pointerEvents: "auto" is the
        // equivalent of Mantine 7's `leftSection`.
        icon: searchIconsDropdown,
        rightSection: rightSectionButtons,
    }

    // Create a new div to append the Search Popdown to via a React Portal
    if (!document.getElementById("popdown-portal")) {
        const divElement = document.createElement("div")
        divElement.setAttribute("id", "popdown-portal")
        // Defaults to position: "static", which prevents resetting stacking context,
        // so we need this position and a z-index change.
        divElement.style.position = "relative"
        // This 3 is necessary to blur above the SideBar's 3 z-index
        // frontend/pages/sidebar/components/SideBar.styles.ts
        divElement.style.zIndex = "3"

        document.body.appendChild(divElement)
    }

    const handleTextInputOnClick = () => {
        // These setTimeout and searchTextInputRef.current updates are so that when
        // the popdown is clicked and the textarea is rendered, the user's cursor is
        // focused on the textarea AND if there is text/a searchInputDefaultValue,
        // the user's cursor will be set to the END of that text.
        setTimeout(() => {
            if (popdownIsOpen || popdownContainerElement) {
                searchTextInputRef.current.selectionStart = searchTextInputRef.current.selectionEnd =
                    searchInputDefaultValue.length
                searchTextInputRef.current.focus()

                if (searchTextInputRef.current) {
                    // checks and adjusts TopNavigation Popdown Container position if needed
                    const textareaOffsetHeight = searchTextInputRef.current.offsetHeight
                    const popdownOffset =
                        textareaOffsetHeight && textareaOffsetHeight > topNavigationFixedHeight
                            ? textareaOffsetHeight - topNavigationFixedHeight
                            : 0

                    popdownContainerElement.style.paddingTop = `${topNavigationFixedHeight + popdownOffset}px`
                }
            }
        }, 0)

        searchInputOnClick()
    }

    return (
        <SearchContext.Provider value={{ onCloseSearchPopdown }}>
            <div ref={searchInputContainerRef} className={classes.textInputContainer}>
                {!popdownIsOpen && (
                    <TextInput
                        {...sharedSearchInputProps}
                        aria-label="Search"
                        onClick={handleTextInputOnClick}
                        ref={searchTextInputRef as unknown as React.RefObject<HTMLInputElement>}
                    />
                )}
                {popdownIsOpen && (
                    <Textarea
                        {...sharedSearchInputProps}
                        aria-label="Search"
                        onClick={searchInputOnClick}
                        autosize
                        minRows={1}
                        maxRows={9}
                        ref={searchTextInputRef as unknown as React.RefObject<HTMLTextAreaElement>}
                    />
                )}
                {popdownIsOpen &&
                    popdownContainerElement &&
                    (aiSearchIsEnabled
                        ? createPortal(
                              <SearchPopdownAi
                                  aiSearchCancelOnClick={aiSearchCancelOnClick}
                                  aiSearchExampleOnClick={aiSearchExampleOnClick}
                                  aiSearchExamples={aiSearchExamples}
                                  aiSearchStatus={aiSearchStatus}
                                  aiSearchToggle={aiSearchToggle}
                                  onCloseSearchPopdown={onCloseSearchPopdown}
                                  searchPopdownRef={searchPopdownRef}
                                  searchTips={searchTips}
                              />,
                              popdownContainerElement,
                          )
                        : createPortal(
                              <SearchPopdownStandard
                                  datasetsOptions={datasetsOptions}
                                  gridItemOnClick={gridItemOnClick}
                                  onCloseSearchPopdown={onCloseSearchPopdown}
                                  searchPopdownRef={searchPopdownRef}
                                  searchTips={searchTips}
                                  segueToSearch={segueToSearch}
                                  setSearchTerm={setSearchTerm}
                                  segueToProfile={segueToProfile}
                                  favorites={favorites}
                                  recents={recents}
                                  favoriteUserAction={favoriteUserAction}
                                  unfavoriteUserAction={unfavoriteUserAction}
                                  archiveUserAction={archiveUserAction}
                                  clearRecents={clearRecents}
                              />,
                              popdownContainerElement,
                          ))}

                <AdvancedSearchTipsModal isOpen={isSearchTipsOpen} searchTips={searchTips} />
            </div>
        </SearchContext.Provider>
    )
}
